import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import styles from './CustomerForm.module.scss'
import { ICustomerFormValue } from './ICustomerFormValue';
import { Button, Divider, Icon, IconButton } from '@mui/material';
import { useKeyboard } from '../../../../components/_keyboard/KeyboardContext';
import KeyboardInput from '../../../../components/_keyboard/_KeyboardInput';
import UseTutorialCard from '../../../../modules/onBoarding/presentation/hooks/UseTutorialCard';
import { CustomerIdentification } from '../../../../modules/device/interface/DeviceProfile';
import { useI18n } from '../../../../hooks/useI18n';
import KeyboardInputMasked from '../../../../components/_keyboard/_KeyboardInputMasked';
import EstablishmentAsCustomerUseCase from '../../../../modules/invoice/application/useCases/EstablishmentAsCustomerUseCase';
import InvoiceService from '../../../../services/api/invoice/InvoiceService';
import UseDeviceStore from '../../../../modules/device/store/DeviceStore';
import { validateCNPJ, validateCPF } from '../../../../utils/Helpers';

interface ICustomerFormErrors {
  name?: string;
  document?: string;
  documentType?: string;
  birthDate?: string;
  phoneNumber?: string;
  email?: string;
  identifier?: string;
  cardTag?: string;
}

export interface ICustomerFormProps {
  //propertys
  onSubmit: (values: ICustomerFormValue) => void
  onClose?: () => void
  title?: string
  handleCustomerData?: CustomerIdentification[]
  orderPad?: boolean;
  invoiceEnabled?: boolean;
}

const CustomerForm: FC<ICustomerFormProps> = ({ onSubmit, onClose, title, handleCustomerData, orderPad = false, invoiceEnabled = false }) => {
  const { t } = useI18n();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const documentTypeInputRef = useRef<HTMLInputElement>(null);
  const birthDateInputRef = useRef<HTMLInputElement>(null);
  const identifierInputRef = useRef<HTMLInputElement>(null);
  const cardTagInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedInputRef, setSelectedInputRef] = useState<React.RefObject<HTMLInputElement>>();
  const [errors, setErrors] = useState({} as ICustomerFormErrors);
  const [documentType, setDocumentType] = useState<"cpf" | "cnpj">("cpf");

  const { openTutorialCard, closeTutorialCard } = UseTutorialCard();
  const [device] = UseDeviceStore(state => [state.device]);

  const divRef = React.useRef<HTMLDivElement>(null);

  const documentData = handleCustomerData?.find(currentData => currentData.identification.value === 2);

  useEffect(() => {
    openTutorialCard({
      element: divRef.current,
      options: {
        title: t('customerForm.tutorial.title'),
        description: t('customerForm.tutorial.description'),
        buttonLabel: t('customerForm.tutorial.buttonLabel'),
        onClose: () => {
          closeTutorialCard();
        }
      }
    })
  }, [closeTutorialCard, openTutorialCard, t]);

  const { configureKeyboard } = useKeyboard();

  const validate = useCallback(async () => {
    let valid = true;
    let errorsData = {} as ICustomerFormErrors;

    if (documentData && documentInputRef.current?.value) {
      const isDocumentValid = documentType === "cpf" ? validateCPF(documentInputRef.current.value) : validateCNPJ(documentInputRef.current.value);

      if (!isDocumentValid) {
        errorsData.document = t('customerForm.errors.invalidDocument');
        valid = false;
      }
    }

    if (!nameInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Nome" && item.required)) {
      errorsData.name = t('customerForm.errors.nameRequired');
      valid = false;
    }
    if (!documentInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "CPF" && item.required)) {
      errorsData.document = t('customerForm.errors.documentRequired');
      valid = false;
    }
    if (!phoneNumberInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Telefone" && item.required)) {
      errorsData.phoneNumber = t('customerForm.errors.phoneRequired');
      valid = false;
    }
    if (!emailInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Email" && item.required)) {
      errorsData.email = t('customerForm.errors.emailRequired');
      valid = false;
    }
    if (!documentTypeInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Etiqueta" && item.required)) {
      errorsData.documentType = t('customerForm.errors.documentTypeRequired');
      valid = false;
    }
    if (!birthDateInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Data" && item.required)) {
      errorsData.birthDate = t('customerForm.errors.birthDateRequired');
      valid = false;
    }
    if (!identifierInputRef.current?.value && handleCustomerData?.find((item) => item.identification.name === "Identificador" && item.required)) {
      errorsData.identifier = t('customerForm.errors.identifierRequired');
      valid = false;
    }
    if (!cardTagInputRef.current?.value && orderPad) {
      errorsData.cardTag = t('customerForm.errors.cardTagRequired');
      valid = false;
    }

    setErrors(errorsData);
    return valid;
  }, [documentData, documentType, handleCustomerData, orderPad, t]);

  const onSubmitHandle = useCallback(async () => {
    const isValid = await validate();
    if (!isValid) return;
    
    const values: ICustomerFormValue = {
      name: nameInputRef.current?.value ?? "",
      document: documentInputRef.current?.value.replace(/\D/g, '') ?? "",
      birthDate: birthDateInputRef.current?.value ?? "",
      phoneNumber: phoneNumberInputRef.current?.value ?? "",
      email: emailInputRef.current?.value ?? "",
      identifier: identifierInputRef.current?.value ?? "",
      documentType: documentInputRef.current?.value ?? "",
      cardTag: cardTagInputRef.current?.value ?? "",
    }

    if (device?.localId && documentType === "cnpj") {
      const invoiceService = InvoiceService();
      EstablishmentAsCustomerUseCase(invoiceService, device?.localId, documentInputRef.current?.value.replace(/\D/g, '') ?? "")
    }
    onSubmit(values);
  }, [device?.localId, documentType, onSubmit, validate]);

  const onChangeFocusKeyboard = useCallback((ev: React.FocusEvent<HTMLInputElement, Element>) => {
    if (ev.target?.id === "name") {
        return setSelectedInputRef(nameInputRef);
    }
    if (ev.target?.id === "document") {
        return setSelectedInputRef(documentInputRef);
    }
    if (ev.target?.id === "phoneNumber") {
        return setSelectedInputRef(phoneNumberInputRef);
    }
    if (ev.target?.id === "email") {
        return setSelectedInputRef(emailInputRef);
    }
    if (ev.target?.id === "birthDate") {
        return setSelectedInputRef(birthDateInputRef);
    }
    if (ev.target?.id === "documentType") {
        return setSelectedInputRef(documentTypeInputRef);
    }
    if (ev.target?.id === "identifier") {
        return setSelectedInputRef(identifierInputRef);
    }
  }, [])

  useEffect(() => {
      configureKeyboard({
          type: 'qwerty',
          inputRef: selectedInputRef,
      })
  }, [configureKeyboard, selectedInputRef])

  useEffect(() => {
      if (!selectedInputRef?.current) {
          setSelectedInputRef(nameInputRef);
      }
  }, [nameInputRef, selectedInputRef]);

  const renderInput = (currentData: CustomerIdentification) => {
    switch (currentData.identification.value) {
      case 1:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='name'
            inputRef={nameInputRef}
            label={t('customerForm.fields.name')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => nameInputRef.current?.focus()}
            error={errors.name}
          />
        );
      case 3:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='phoneNumber'
            inputRef={phoneNumberInputRef}
            label={t('customerForm.fields.phoneNumber')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => phoneNumberInputRef.current?.focus()}
            error={errors.phoneNumber}
          />
        );
      case 4:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='email'
            inputRef={emailInputRef}
            label={t('customerForm.fields.email')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => emailInputRef.current?.focus()}
            error={errors.email}
          />
        );
      case 5:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='birthDate'
            inputRef={birthDateInputRef}
            label={t('customerForm.fields.birthDate')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => birthDateInputRef.current?.focus()}
            error={errors.birthDate}
          />
        );
      case 6:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='documentType'
            inputRef={documentTypeInputRef}
            label={t('customerForm.fields.documentType')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => documentTypeInputRef.current?.focus()}
            error={errors.documentType}
          />
        );
      case 7:
        return currentData.enabled && (
          <KeyboardInput
            key={currentData.identification.value}
            id='identifier'
            inputRef={identifierInputRef}
            label={t('customerForm.fields.identifier')}
            required={currentData.required}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => identifierInputRef.current?.focus()}
            error={errors.identifier}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div id={styles.CustomerForm}>
      <div className={styles.container} >
        <div className={styles.header}>
          <h1>
            {handleCustomerData?.filter(item => item.enabled).length === 1 && invoiceEnabled
              ? t('customerForm.invoiceTitle')
              : orderPad
              ? t('customerForm.orderPadTitle')
              : (title || t('customerForm.title'))
            }
          </h1>
          <IconButton onClick={onClose}><Icon>close</Icon></IconButton>
        </div>
        {handleCustomerData?.map((currentData) => renderInput(currentData))}
        {orderPad && 
          <KeyboardInput
            id='cardTag'
            inputRef={cardTagInputRef}
            label={t('customerForm.fields.cardTag')}
            required={true}
            onFocus={onChangeFocusKeyboard}
            onSubmit={() => cardTagInputRef.current?.focus()}
            error={errors.cardTag}
          />
        }
        {documentData && (
          <div>
            {handleCustomerData?.filter(item => item.enabled).length !== 1 && invoiceEnabled && 
              <div className={styles.invoiceInfo}>
                <Divider style={{ flex: 1 }} />
                <span>Emissão fiscal</span>
                <Divider style={{ flex: 1 }} />
              </div>
            }
            <div className={styles.typeContainer}>
              <span className={documentData.required ? styles.required : ""}>
                {t('customerForm.fields.document')}
              </span>
              <div>
                <button
                  onClick={() => setDocumentType("cpf")}
                  className={documentType === "cpf" ? styles.typeActive : styles.type}
                >
                  {t('customerForm.fields.cpf')}
                </button>
                <button
                  onClick={() => setDocumentType("cnpj")}
                  className={documentType === "cnpj" ? styles.typeActive : styles.type}
                >
                  {t('customerForm.fields.cnpj')}
                </button>
              </div>
            </div>
            <KeyboardInputMasked
              key={documentData.identification.value}
              id='document'
              inputRef={documentInputRef}
              onFocus={onChangeFocusKeyboard}
              onSubmit={() => documentInputRef.current?.focus()}
              error={errors.document}
              mask={documentType === "cpf" ? "999.999.999-99" : "99.999.999/9999-99"}
            />
          </div>
        )}
        <Button ref={submitButtonRef} size='large' variant='contained' onClick={onSubmitHandle}>{t('customerForm.buttons.continue')}</Button>
        {/* validar comanda no botão */}
      </div>
    </div>
  )
}

export default CustomerForm;